import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { useI18n } from '../../i18n';
import { useModalContext } from './modalContext';
import { ChevronDownIcon } from './Icons';

/** #675, #693 — one record action of a modal (Archive, Restore, Remove from
 *  team, Delete, Merge). One action shows as a text button on the left of the
 *  footer. Two or more actions are the items of the Actions menu. The handler
 *  asks its own question (confirm) before it writes. */
export interface ModalAction {
  /** The visible text. A string of `t.*`. */
  label: string;
  onClick: () => void;
  /** Red text for an action that removes or ends a record. Neutral otherwise. */
  destructive?: boolean;
  /** The action is shown but refused (a busy write, a lock). */
  disabled?: boolean;
  /** The `data-testid` of the button. */
  testId: string;
  /** The text of the tooltip, when it adds to the label. */
  title?: string;
}

interface ModalFooterProps {
  /** #675, #693 — the record actions of the modal, as ONE control on the
   *  left. One action is a text button: a long label wraps inside the button.
   *  Two or more actions are the items of the Actions menu. While `busy` is
   *  true, they are off. With `secondary` too, the left group is the actions
   *  and then `secondary`. */
  actions?: ModalAction[];
  onSave?: () => void;
  /** Override the Save button label (e.g. "Create", "Reassign"). Defaults to t.common.save.
   *  This is the label while idle: the busy label comes from `busy` (#661, F08).
   *  A text, or a text with an icon (#603: the arrow of a step is an SVG). */
  saveLabel?: ReactNode;
  /** #552, #661 (F08) — true while the write runs: the primary is off and shows
   *  `busyLabel`. `saveDisabled` keeps the other conditions (an empty field, a
   *  step that is not ready). Pass the `busy` of `useBusy()`, or a flag that
   *  the caller sets before the first await. */
  busy?: boolean;
  /** The text of the primary while `busy`. Default `t.common.saving`. Give one
   *  only when the work has its own name (Creating bouts, Generating). */
  busyLabel?: ReactNode;
  /** Submit-style button — when true, renders <button type="submit"> with no onClick, so
   *  the parent <form> handles submission. */
  saveAsSubmit?: boolean;
  /** The id of the <form> that the submit button sends, when the button is
   *  not inside that form. */
  saveForm?: string;
  saveDisabled?: boolean;
  onCancel: () => void;
  /** t.common.close for a read-only view. */
  cancelLabel?: string;
  /** #552 — the other actions (Back, Analysis, Draw again, a guide link, a
   *  count), on the left. The main action never goes here. */
  secondary?: React.ReactNode;
  /** #588 — a second action on the right, next to the main action (for
   *  example "Apply Ring Referees" next to "Apply All"). */
  beside?: React.ReactNode;
  /** #571 — a ref to the Save button, for a modal that gives its footer a
   *  key for each step and must move the focus to the new Save. */
  saveRef?: React.Ref<HTMLButtonElement>;
  /** #589 — the data-testid of the Save button. The default `modal-save`
   *  stays for every modal; a modal that opens on top of another one
   *  (ConfirmModal) names its own button. */
  saveTestId?: string;
  /** #665 — a red Save (`btn-danger`) for an action that removes or ends a
   *  record. ConfirmModal sets it for a destructive question. */
  saveDanger?: boolean;
  /** #665 — the button that gets the first focus of the modal. It carries
   *  `data-autofocus` (useModalA11y). With no value, the first field or the
   *  close X gets the first focus. */
  focusFirst?: 'save' | 'cancel';
  /** #665 — the data-testid of the Cancel button. */
  cancelTestId?: string;
}

/** #693 — the record actions of a modal with two or more actions: one text
 *  button, "Actions", that opens a menu above it.
 *
 *  - The menu has the role `menu`, and each action has the role `menuitem`.
 *    An item keeps the `data-testid`, the red text and the disabled state of
 *    its action. The menu is in the DOM only while it is open.
 *  - A click on the button or Enter, Space or ArrowDown opens the menu and
 *    gives the focus to the first item that is on. ArrowUp gives it to the
 *    last item. In the menu, ArrowUp and ArrowDown move the focus (they wrap at
 *    the ends), Home and End go to the first and the last item, and Enter or
 *    Space runs the item.
 *  - Esc, Tab and a press outside the menu close the menu. Esc and the press
 *    close the menu only, never the modal: the listeners are on the document in
 *    the capture phase, so they run before the Esc listener of `useModalA11y`
 *    and before the backdrop.
 *  - A press outside closes the menu at `pointerdown`, because iOS Safari can
 *    send no click for a tap on plain text. The click of the same tap does not
 *    reach its target: the menu stops it. Thus one tap closes one thing, and a
 *    tap on the backdrop closes the menu and not the modal. The next press or
 *    key clears this stop, so a tap with no click stops no later click. A click
 *    outside with no press before it (a script) also closes the menu.
 *  - The focus goes back to the button when the menu closes, also before an
 *    action runs. The question of the action (`useConfirm()`) then gives the
 *    focus back to the button when it closes. A press outside on a field
 *    leaves the focus on that field.
 *  - The button is off while `busy` is true or every action is off. The menu
 *    closes when the button goes off.
 *  - A held Enter or Space repeats its keydown. The menu ignores a repeated
 *    Enter or Space, so the key that opens the menu does not also run the
 *    first item. */
function ActionsMenu({ actions, busy }: { actions: ModalAction[]; busy: boolean }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  /** The item that gets the focus when the menu opens. */
  const startAt = useRef<'first' | 'last'>('first');
  /** A press outside closed the menu: the click of the same tap stops. */
  const stopClick = useRef(false);
  const buttonId = useId();
  const menuId = useId();
  const off = busy || actions.every(a => a.disabled);

  const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []);

  function close(focusButton: boolean) {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus({ preventScroll: true });
  }

  /** After a press outside: a field keeps the focus. Else (the backdrop, the
   *  dialog itself, the body) the button gets it back. */
  function focusAfterPress() {
    const active = document.activeElement;
    const onField = active instanceof HTMLElement && active !== document.body
      && active.getAttribute('role') !== 'dialog' && !rootRef.current?.contains(active);
    if (!onField) buttonRef.current?.focus({ preventScroll: true });
  }

  // The first focus in the open menu. With every item off, the menu holds it.
  useLayoutEffect(() => {
    if (!open) return;
    const list = items();
    const target = startAt.current === 'last' ? list[list.length - 1] : list[0];
    (target ?? menuRef.current)?.focus({ preventScroll: true });
  }, [open]);

  // The button goes off (a write runs, or every action is off): the menu
  // closes. An item with the focus goes away, so the dialog gets the focus.
  useEffect(() => {
    if (!off || !open) return;
    // A disabled item can lose the focus before this effect runs: then the
    // focus is on the body, and the dialog takes it too.
    const active = document.activeElement;
    const hadFocus = !active || active === document.body || !!menuRef.current?.contains(active);
    setOpen(false);
    if (hadFocus) rootRef.current?.closest<HTMLElement>('[role=dialog]')?.focus({ preventScroll: true });
  }, [off, open]);

  // The stop of the click after a press outside. These listeners stay while
  // the menu is closed: the click comes after the press closed the menu.
  useEffect(() => {
    function clear() { stopClick.current = false; }
    function onClick(e: MouseEvent) {
      if (!stopClick.current) return;
      stopClick.current = false;
      // Only the click of a real press (detail 1 or more) is the same tap. A
      // keyboard, voice-control or screen-reader click (detail 0) goes through.
      if (e.detail === 0) return;
      e.preventDefault();
      e.stopPropagation();
      focusAfterPress();
    }
    document.addEventListener('pointerdown', clear, true);
    document.addEventListener('keydown', clear, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('pointerdown', clear, true);
      document.removeEventListener('keydown', clear, true);
      document.removeEventListener('click', onClick, true);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      close(true);
    }
    // Added after the listeners above, so it runs after `clear`.
    function onPress(e: PointerEvent) {
      if (rootRef.current?.contains(e.target as Node)) return;
      stopClick.current = true;
      // An item with the focus goes away. The press can then move the focus
      // to a field; the click of the tap checks it again.
      close(!!menuRef.current?.contains(document.activeElement));
    }
    function onClick(e: MouseEvent) {
      if (rootRef.current?.contains(e.target as Node)) return;
      e.preventDefault();
      e.stopPropagation();
      close(false);
      focusAfterPress();
    }
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPress, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPress, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [open]);

  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    // A held Enter or Space: only the first keydown acts. Else the repeat of
    // the key that opened the menu runs the first item.
    if (e.repeat && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      return;
    }
    const onButton = e.target === buttonRef.current;
    if (!open) {
      if (onButton && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        startAt.current = e.key === 'ArrowUp' ? 'last' : 'first';
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Tab') {
      // From the button, Tab goes on to the next control of the modal. From
      // an item, the focus goes back to the button.
      if (!onButton) {
        e.preventDefault();
        e.stopPropagation();
      }
      close(!onButton);
      return;
    }
    const list = items();
    if (list.length === 0) return;
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    let next: HTMLButtonElement | undefined;
    if (e.key === 'ArrowDown') next = at < 0 ? list[0] : list[(at + 1) % list.length];
    else if (e.key === 'ArrowUp') next = at < 0 ? list[list.length - 1] : list[(at - 1 + list.length) % list.length];
    else if (e.key === 'Home') next = list[0];
    else if (e.key === 'End') next = list[list.length - 1];
    if (!next) return;
    e.preventDefault();
    next.focus({ preventScroll: true });
  }

  function run(a: ModalAction) {
    close(true);
    a.onClick();
  }

  return (
    <div ref={rootRef} className="relative -ml-3" onKeyDown={onKeyDown}>
      <button ref={buttonRef} id={buttonId} type="button" data-testid="modal-actions"
        aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined}
        disabled={off} className="btn-text btn-sm"
        onClick={() => { startAt.current = 'first'; setOpen(o => !o); }}>
        {t.common.actions}
        <ChevronDownIcon className="w-3 h-3" />
      </button>
      {open && (
        <div ref={menuRef} id={menuId} role="menu" aria-labelledby={buttonId} tabIndex={-1}
          className="absolute bottom-full left-0 z-20 mb-1 flex w-max max-w-xs flex-col whitespace-normal rounded-lg border border-gray-200 bg-white p-1 shadow-lg focus:outline-hidden">
          {actions.map(a => (
            <button key={a.testId} type="button" role="menuitem" tabIndex={-1}
              onClick={() => run(a)} disabled={a.disabled || busy}
              title={a.title} data-testid={a.testId}
              className={`${a.destructive ? 'btn-text-red' : 'btn-text'} btn-sm justify-start text-left`}>
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Uniform modal footer: [Save] [Cancel], right-aligned, content-width, btn-sm.
 *  Save is on the left of the pair, Cancel on the right. Both buttons share the
 *  same size; primary action stands out via filled style, not via width.
 *
 *  #693 — the footer has two groups. The left group holds one control (the
 *  single action, or the Actions menu) and then `secondary`. The right group
 *  holds `beside`, Save and Cancel. The rule:
 *  - The two groups are in one row when they fit. The right group then has
 *    its full width: no label of it wraps.
 *  - The left group takes the remaining space. Its controls wrap onto stacked
 *    lines inside it. Only the single action can wrap its label, and it gets
 *    at least 7rem, so its label takes two lines at most. The other left
 *    controls keep their label on one line.
 *  - When the right group does not fit beside the left group, the right group
 *    goes onto its own line below, at the right. The left group stays at the
 *    left. When the right group does not fit on its
 *    own line either, `beside` goes above Save and Cancel. Save and Cancel
 *    stay together on one line.
 *  - The footer never scrolls sideways.
 *  In a 'panel' modal the footer stays at the bottom of the dialog, with a
 *  border above it. */
export default function ModalFooter({
  actions, onSave, saveLabel, busy = false, busyLabel, saveAsSubmit, saveForm, saveDisabled, onCancel, cancelLabel, secondary, beside, saveRef, saveTestId,
  saveDanger = false, focusFirst, cancelTestId,
}: ModalFooterProps) {
  const { t } = useI18n();
  const hasActions = !!actions && actions.length > 0;
  const modal = useModalContext();
  // #693 — the row wraps only between the two groups (see the rule above).
  const root = modal?.layout === 'panel'
    ? 'shrink-0 flex flex-wrap items-center justify-end gap-2 px-5 py-3 border-t border-gray-100'
    : 'flex flex-wrap items-center justify-end gap-2 mt-5';
  const single = hasActions && actions!.length === 1 ? actions![0] : null;
  // #693 — the left group. The row breaks with its flex-basis, which is never
  // more than its content (max-w-max) and never less than its widest control
  // (the automatic minimum). With the single action the basis is 7rem: its
  // label can wrap, and 7rem keeps it at two lines at most. Else the basis is
  // 0, and each control keeps its label on one line (whitespace-nowrap).
  // mr-auto keeps the group at the left also on a line of its own.
  const left = `mr-auto flex grow ${single ? 'basis-28' : 'basis-0'} max-w-max flex-wrap items-center gap-x-2 gap-y-1 whitespace-nowrap`;
  return (
    <div className={root} data-testid="modal-footer">
      {hasActions || secondary ? (
        <div className={left} data-testid="modal-footer-left">
          {single ? (
            <button type="button" onClick={single.onClick} disabled={single.disabled || busy}
              title={single.title} data-testid={single.testId}
              className={`${single.destructive ? 'btn-text-red' : 'btn-text'} btn-sm -ml-3 text-left whitespace-normal`}>
              {single.label}
            </button>
          ) : hasActions ? (
            <ActionsMenu actions={actions!} busy={busy} />
          ) : null}
          {secondary}
        </div>
      ) : null}
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2" data-testid="modal-footer-right">
        {beside}
        {/* Save and Cancel: one pair, never split. */}
        <div className="flex items-center gap-2">
          {onSave || saveAsSubmit ? (
            <button
              ref={saveRef}
              type={saveAsSubmit ? 'submit' : 'button'}
              form={saveAsSubmit ? saveForm : undefined}
              onClick={saveAsSubmit ? undefined : onSave}
              disabled={busy || saveDisabled}
              data-testid={saveTestId ?? 'modal-save'}
              data-autofocus={focusFirst === 'save' || undefined}
              className={`${saveDanger ? 'btn-danger' : 'btn-primary'} btn-sm`}
            >
              {busy ? (busyLabel ?? t.common.saving) : (saveLabel ?? t.common.save)}
            </button>
          ) : null}
          <button type="button" onClick={onCancel} className="btn-outline btn-sm"
            data-testid={cancelTestId} data-autofocus={focusFirst === 'cancel' || undefined}>
            {cancelLabel ?? t.common.cancel}
          </button>
        </div>
      </div>
    </div>
  );
}
